// AILabTools API Service
// Automatic try-on service - no manual images needed!

import axios from 'axios';

const AILAB_API_KEY = process.env.REACT_APP_AILAB_API_KEY || '';
const AILAB_API_BASE_URL = 'https://www.ailabapi.com/api';

class AILabToolsService {
  /**
   * Get available hairstyles from API
   * @returns {Promise<Array>} List of available hairstyles
   */
  async getAvailableHairstyles() {
    try {
      // If API key not set, return default styles
      if (!AILAB_API_KEY) {
        console.warn('AILabTools API key not set. Using default styles.');
        return this.getDefaultHairstyles();
      }

      const response = await axios.get(`${AILAB_API_BASE_URL}/portrait/effects/hairstyle-editor-pro/styles`, {
        headers: {
          'ailabapi-api-key': AILAB_API_KEY
        }
      });

      // API might return data in different formats
      const styles = response.data?.data?.styles || 
                     response.data?.styles || 
                     response.data?.list ||
                     response.data || [];
      
      // Return API styles if available, otherwise defaults
      if (styles && styles.length > 0) {
        return styles;
      }
      return this.getDefaultHairstyles();
    } catch (error) {
      console.error('Error fetching hairstyles from AILabTools:', error);
      // Fallback to default styles
      return this.getDefaultHairstyles();
    }
  }

  /**
   * Get available makeup styles from API
   * @returns {Promise<Array>} List of available makeup styles
   */
  async getAvailableMakeupStyles() {
    try {
      if (!AILAB_API_KEY) {
        console.warn('AILabTools API key not set. Using default styles.');
        return this.getDefaultMakeupStyles();
      }

      const response = await axios.get(`${AILAB_API_BASE_URL}/portrait/effects/makeup/styles`, {
        headers: {
          'ailabapi-api-key': AILAB_API_KEY
        }
      });

      // API might return data in different formats
      const styles = response.data?.data?.styles || 
                     response.data?.styles || 
                     response.data?.list ||
                     response.data || [];
      
      // Return API styles if available, otherwise defaults
      if (styles && styles.length > 0) {
        return styles;
      }
      return this.getDefaultMakeupStyles();
    } catch (error) {
      console.error('Error fetching makeup styles from AILabTools:', error);
      return this.getDefaultMakeupStyles();
    }
  }

  /**
   * Apply hairstyle to user's photo (async task pattern)
   * @param {File|string} userPhoto - User's photo (File object or base64/data URL)
   * @param {string} styleId - Hairstyle ID from API
   * @returns {Promise<string>} Transformed image URL or data URL
   */
  async applyHairstyle(userPhoto, styleId) {
    try {
      if (!AILAB_API_KEY) {
        throw new Error('AILabTools API key not configured. Please set REACT_APP_AILAB_API_KEY in your .env file.');
      }

      // Convert photo to File if it's a data URL
      let photoFile = userPhoto;
      if (typeof userPhoto === 'string' && userPhoto.startsWith('data:')) {
        photoFile = await this.dataURLtoFile(userPhoto, 'user-photo.jpg');
      }

      // Step 1: Submit task and get task_id
      // API expects 'hair_style' parameter with exact style names like "BobCut", "BuzzCut", etc.
      const formData = new FormData();
      formData.append('image', photoFile);
      // Convert style ID to API style name (e.g., "bob-cut-001" -> "BobCut")
      const hairStyleName = this.mapStyleIdToApiName(styleId);
      formData.append('hair_style', hairStyleName);
      // Some APIs require task_type for async
      formData.append('task_type', 'async');

      const submitResponse = await axios.post(
        `${AILAB_API_BASE_URL}/portrait/effects/hairstyle-editor-pro`,
        formData,
        {
          headers: {
            'ailabapi-api-key': AILAB_API_KEY
            // Don't set Content-Type - let axios set it automatically for FormData
          },
          timeout: 30000
        }
      );

      console.log('✅ Task submitted successfully:', submitResponse.data);
      
      const taskId = submitResponse.data?.task_id || submitResponse.data?.data?.task_id;
      if (!taskId) {
        console.error('❌ No task_id in response:', submitResponse.data);
        throw new Error('No task_id returned from API. Response: ' + JSON.stringify(submitResponse.data));
      }
      
      console.log('🆔 Task ID received:', taskId);

      // Step 2: Poll for results (async task pattern)
      return await this.pollTaskResult(taskId);
    } catch (error) {
      console.error('❌ Error applying hairstyle:', error);
      
      // Log detailed error for debugging
      if (error.response) {
        console.error('📋 API Response Error Details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.config?.url
        });
      }
      
      // Provide user-friendly error messages
      const errorData = error.response?.data;
      const errorCode = errorData?.error_detail?.code || errorData?.error_code_str || '';
      const errorMessage = errorData?.error_msg || errorData?.error_detail?.message || errorData?.message || '';
      
      console.log('🔍 Error Analysis:', {
        errorCode,
        errorMessage,
        status: error.response?.status,
        isFaceDetectionError: errorCode === 'ERROR_FACE_SIZE_RATIO_NOT_MET' || 
                              (errorMessage && (
                                errorMessage.toLowerCase().includes('facial area') || 
                                errorMessage.toLowerCase().includes('face size') ||
                                errorMessage.toLowerCase().includes('face must') ||
                                errorMessage.toLowerCase().includes('face should')
                              ))
      });
      
      // Handle specific error codes with helpful messages - ONLY for actual face detection errors
      if (errorCode === 'ERROR_FACE_SIZE_RATIO_NOT_MET' || 
          (errorMessage && (
            errorMessage.toLowerCase().includes('facial area') || 
            errorMessage.toLowerCase().includes('face size') ||
            errorMessage.toLowerCase().includes('face must') ||
            errorMessage.toLowerCase().includes('face should')
          ))) {
        // Note: We allow back hair photos to be submitted, but API may reject them
        console.log('⚠️ Face detection error detected');
        throw new Error(
          'The API requires a face to be visible in the photo (at least 10% of image). ' +
          'For best results, use a front-facing photo with your face clearly visible. ' +
          'Back hair photos may not work with this API.'
        );
      }
      
      // Generic error handling - show actual error message from API
      const userFriendlyError = errorMessage || 
                                errorData?.error || 
                                errorData?.msg ||
                                (error.response?.status === 400 ? 'Invalid request. Please check your photo format and try again.' : '') ||
                                (error.response?.status === 422 ? 'The API rejected your photo. Please try a different photo.' : '') ||
                                (error.response?.status === 500 ? 'Server error. Please try again later.' : '') ||
                                (error.response?.status ? `API Error (${error.response.status})` : '') ||
                                error.message || 
                                'Failed to apply hairstyle. Please try again with a different photo.';
      
      console.log('📤 Throwing error:', userFriendlyError);
      throw new Error(userFriendlyError);
    }
  }

  /**
   * Poll async task result until complete
   * @param {string} taskId - Task ID from API
   * @returns {Promise<string>} Result image URL or data URL
   */
  async pollTaskResult(taskId, maxAttempts = 30, interval = 3000) {
    console.log(`🔄 Starting to poll task: ${taskId} (max ${maxAttempts} attempts, ${interval}ms interval)`);
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Try different parameter formats - API might expect different format
        const response = await axios.get(
          `${AILAB_API_BASE_URL}/common/query-async-task-result`,
          {
            params: { 
              task_id: taskId
            },
            headers: {
              'ailabapi-api-key': AILAB_API_KEY
            },
            timeout: 15000
          }
        );
        
        console.log(`📊 Poll attempt ${attempt + 1}/${maxAttempts}:`, {
          status: response.status,
          data: response.data
        });

        // Handle different response formats
        // API returns: { data: { data: { task_status: 2, images: [...] } } }
        // OR: { data: { task_status: 2, images: [...] } }
        const outerData = response.data;
        const innerData = outerData?.data || outerData;
        
        // task_status can be at different levels
        const taskStatus = innerData?.task_status || 
                          outerData?.task_status ||
                          innerData?.status || 
                          innerData?.taskStatus ||
                          outerData?.status;
        
        console.log(`📈 Task status: ${taskStatus} (attempt ${attempt + 1})`);
        console.log('📦 Response structure:', {
          'outerData': outerData,
          'innerData': innerData,
          'task_status': taskStatus,
          'has_images': !!(innerData?.images || outerData?.images)
        });
        
        // Status: 0 = queued, 1 = processing, 2 = complete, 3 = failed
        if (taskStatus === 2 || taskStatus === 'completed' || taskStatus === 'success' || taskStatus === 200) {
          // Task complete - return result
          console.log('✅ Task completed! Extracting result...');
          
          // Check for images array first (AILabTools format: data.data.images[0])
          const imagesArray = innerData?.images || 
                             outerData?.images ||
                             innerData?.data?.images ||
                             outerData?.data?.images;
          
          if (imagesArray && Array.isArray(imagesArray) && imagesArray.length > 0) {
            const imageUrl = imagesArray[0];
            console.log('✅ Got result image URL from images array:', imageUrl);
            return imageUrl;
          }
          
          // Fallback to other formats
          const resultImageUrl = innerData?.result_image_url || 
                                innerData?.image_url || 
                                innerData?.result_url ||
                                outerData?.result_image_url ||
                                outerData?.image_url ||
                                innerData?.result?.image_url ||
                                innerData?.result?.url;
          
          const resultImageBase64 = innerData?.result_image_base64 || 
                                   innerData?.image_base64 || 
                                   innerData?.base64 ||
                                   outerData?.result_image_base64 ||
                                   outerData?.image_base64;
          
          if (resultImageUrl) {
            console.log('✅ Got result image URL:', resultImageUrl);
            return resultImageUrl;
          } else if (resultImageBase64) {
            console.log('✅ Got result image base64 (length:', resultImageBase64.length, ')');
            return `data:image/jpeg;base64,${resultImageBase64}`;
          } else {
            // Check if result is in different format
            const result = innerData?.result || outerData?.result || innerData || outerData;
            if (result?.image_url) {
              console.log('✅ Got result from nested result.image_url');
              return result.image_url;
            }
            if (result?.image_base64) {
              console.log('✅ Got result from nested result.image_base64');
              return `data:image/jpeg;base64,${result.image_base64}`;
            }
            if (result?.images && Array.isArray(result.images) && result.images.length > 0) {
              console.log('✅ Got result from nested result.images array');
              return result.images[0];
            }
            
            console.error('❌ No result image found. Full response:', JSON.stringify(response.data, null, 2));
            throw new Error('No result image in completed task. Response: ' + JSON.stringify(response.data));
          }
        } else if (taskStatus === 1 || taskStatus === 'processing' || taskStatus === 'in_progress') {
          // Still processing - wait and retry
          console.log(`⏳ Task still processing... (${attempt + 1}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        } else if (taskStatus === 0 || taskStatus === 'queued' || taskStatus === 'pending' || taskStatus === 'waiting') {
          // Queued - wait and retry
          console.log(`⏳ Task queued... (${attempt + 1}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        } else if (taskStatus === 3 || taskStatus === 'failed' || taskStatus === 'error' || taskStatus === 'failure') {
          // Task failed
          const errorMsg = innerData?.error_message || 
                          innerData?.message || 
                          innerData?.error || 
                          innerData?.error_msg ||
                          outerData?.error_message ||
                          outerData?.message ||
                          'Task failed';
          console.error('❌ Task failed:', errorMsg);
          throw new Error(`Task failed: ${errorMsg}`);
        } else {
          // Unknown status - but check if we have images anyway (sometimes status might be missing but result is ready)
          const imagesArray = innerData?.images || outerData?.images;
          if (imagesArray && Array.isArray(imagesArray) && imagesArray.length > 0) {
            console.log('✅ Found images array even though status is unknown, returning result');
            return imagesArray[0];
          }
          
          // Unknown status - log and continue polling
          console.warn(`⚠️ Unknown task status: ${taskStatus}, continuing to poll... (${attempt + 1}/${maxAttempts})`);
          console.warn('Full response data:', JSON.stringify({ outerData, innerData }, null, 2));
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
      } catch (error) {
        // Handle 422 and other errors
        if (error.response?.status === 422) {
          // 422 might mean task_id format is wrong or task doesn't exist
          const errorData = error.response?.data;
          console.error('422 Error polling task:', errorData);
          
          // Try alternative endpoint format (path parameter instead of query)
          if (attempt === 0) {
            try {
              const altResponse = await axios.get(
                `${AILAB_API_BASE_URL}/common/query-async-task-result/${taskId}`,
                {
                  headers: {
                    'ailabapi-api-key': AILAB_API_KEY
                  },
                  timeout: 10000
                }
              );
              // If this works, use it
              const altData = altResponse.data?.data || altResponse.data;
              if (altData?.task_status === 2) {
                const resultImageUrl = altData?.result_image_url || altData?.image_url;
                const resultImageBase64 = altData?.result_image_base64 || altData?.image_base64;
                if (resultImageUrl) return resultImageUrl;
                if (resultImageBase64) return `data:image/jpeg;base64,${resultImageBase64}`;
              }
            } catch (altError) {
              console.warn('Alternative endpoint also failed:', altError.message);
            }
          }
          
          // Check if it's a face detection error - ONLY show this for actual face detection errors
          const errorCode = errorData?.error_detail?.code || errorData?.error_code_str || '';
          const errorMessage = errorData?.error_msg || errorData?.error_detail?.message || '';
          
          console.log('422 Error details:', {
            errorCode,
            errorMessage,
            fullError: errorData
          });
          
          // ONLY throw face detection error if it's specifically a face detection error
          if (errorCode === 'ERROR_FACE_SIZE_RATIO_NOT_MET' || 
              (errorMessage && (
                errorMessage.toLowerCase().includes('facial area') || 
                errorMessage.toLowerCase().includes('face size') ||
                errorMessage.toLowerCase().includes('face must') ||
                errorMessage.toLowerCase().includes('face should')
              ))) {
            // Face detection requirement error - throw with helpful message
            throw new Error(
              'The API requires a face to be visible in the photo (at least 10% of image). ' +
              'For best results, use a front-facing photo with your face clearly visible. ' +
              'Back hair photos may not work with this API.'
            );
          }
          
          // For other 422 errors, show the actual error message
          console.warn('422 Error is NOT a face detection error:', errorData);
          
          // If still failing after retries, throw error with helpful message
          if (attempt >= maxAttempts - 1) {
            const userFriendlyMsg = errorData?.error_msg || 
                                   errorData?.error_detail?.message || 
                                   errorData?.message ||
                                   'The API rejected your photo. Please try a different photo.';
            throw new Error(`Failed to process photo: ${userFriendlyMsg}`);
          }
          
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
        
        // For other errors, retry if not last attempt
        if (attempt < maxAttempts - 1) {
          console.warn(`Polling attempt ${attempt + 1} failed:`, error.response?.status, error.message);
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
        throw new Error(`Failed to get task result after ${maxAttempts} attempts: ${error.message}`);
      }
    }
    
    // If we get here, we've exhausted all attempts
    console.error(`❌ Task ${taskId} did not complete after ${maxAttempts} attempts (${(maxAttempts * interval) / 1000}s)`);
    throw new Error(`Task did not complete within timeout period (${(maxAttempts * interval) / 1000} seconds). Task ID: ${taskId}`);
  }

  /**
   * Apply makeup to user's photo
   * @param {File|string} userPhoto - User's photo
   * @param {string} styleId - Makeup style ID from API
   * @returns {Promise<string>} Transformed image URL or data URL
   */
  async applyMakeup(userPhoto, styleId) {
    try {
      if (!AILAB_API_KEY) {
        throw new Error('AILabTools API key not configured. Please set REACT_APP_AILAB_API_KEY in your .env file.');
      }

      let photoFile = userPhoto;
      if (typeof userPhoto === 'string' && userPhoto.startsWith('data:')) {
        photoFile = await this.dataURLtoFile(userPhoto, 'user-photo.jpg');
      }

      // Step 1: Submit task and get task_id
      const formData = new FormData();
      formData.append('image', photoFile); // Parameter name might be 'image' not 'photo'
      formData.append('makeup_style_id', styleId);
      // Some APIs require task_type for async
      formData.append('task_type', 'async');

      const submitResponse = await axios.post(
        `${AILAB_API_BASE_URL}/portrait/effects/makeup`,
        formData,
        {
          headers: {
            'ailabapi-api-key': AILAB_API_KEY
            // Don't set Content-Type - let axios set it automatically for FormData
          },
          timeout: 30000
        }
      );

      const taskId = submitResponse.data?.task_id || submitResponse.data?.data?.task_id;
      if (!taskId) {
        throw new Error('No task_id returned from API. Response: ' + JSON.stringify(submitResponse.data));
      }

      // Step 2: Poll for results (async task pattern)
      return await this.pollTaskResult(taskId);
    } catch (error) {
      console.error('Error applying makeup:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to apply makeup. Please try again.'
      );
    }
  }

  /**
   * Map style ID to API style name
   * API expects exact style names like "BobCut", "BuzzCut", etc.
   * @param {string} styleId - Style ID (e.g., "bob-cut-001" or "BobCut")
   * @returns {string} API style name
   */
  mapStyleIdToApiName(styleId) {
    // If already in correct format (e.g., "BobCut"), return as-is
    if (styleId && /^[A-Z][a-zA-Z0-9_-]*$/.test(styleId)) {
      // Check if it's a valid API style name (starts with capital letter)
      const validNames = [
        'BuzzCut', 'UnderCut', 'Pompadour', 'SlickBack', 'CurlyShag', 'WavyShag',
        'FauxHawk', 'Spiky', 'CombOver', 'HighTightFade', 'ManBun', 'Afro',
        'LowFade', 'UndercutLongHair', 'TwoBlockHaircut', 'TexturedFringe',
        'BluntBowlCut', 'LongWavyCurtainBangs', 'MessyTousled', 'CornrowBraids',
        'LongHairTiedUp', 'Middle-parted', 'ShortPixieWithShavedSides', 'ShortNeatBob',
        'DoubleBun', 'Updo', 'Spiked', 'bowlCut', 'Chignon', 'PixieCut',
        'SlickedBack', 'LongCurly', 'CurlyBob', 'StackedCurlsInShortBob',
        'SidePartCombOverHairstyleWithHighFade', 'WavyFrenchBobVibesfrom1920',
        'BobCut', 'ShortTwintails', 'ShortCurlyPixie', 'LongStraight', 'LongWavy',
        'FishtailBraid', 'TwinBraids', 'Ponytail', 'Dreadlocks', 'Cornrows',
        'ShoulderLengthHair', 'LooseCurlyAfro', 'LongTwintails', 'LongHimeCut',
        'BoxBraids', 'Side-Parted_Waves', 'Magic_Perm', 'Vintage_Curls',
        'Mushroom_Curl', 'Textured_Crop', 'Side_Flip_Perm', 'Layered_Waves',
        'Comma_Hair', 'Korean_Wavy_Crop', 'Smooth_Crop', 'Natural_Middle_Part',
        'Side-Part_Crop', 'FreshSide-Parted', 'FluffyMiddlePart', 'Natural_Side-Part',
        'Wolf_Crop', 'Wind-Tousled_Crop', 'Side-Parted_Textured', 'Neat_Curly_Crop_Cut',
        'Elegant_Volumized_Bob', 'Modern_Curls_Chic', 'Mocha_Volume_Pixie',
        'Elegant_Side_Flow', 'Light_Inward_Bob', 'Soft_Wavy_Bob', 'Retro_Airy_Curl',
        'Elegant_Smooth_Bob', 'Elegant_Soft_Curl', 'Playful_Wavy_Bob',
        'Playful_Curly_Bob', 'Airy_Short_Curls', 'Curved_Chic_Bob', 'Executive_Pixie',
        'Soft_Layered_Curl', 'Elegant_Side_Wave', 'Chic_Wavy_Pixie', 'Elegant_Wavy_Crop',
        'Edgy_Textured_Pixie', 'Chic_Tapered_Bob', 'Natural_Short', 'Neat_Short',
        'Smooth_Inward_Bob', 'MediumLengthWavy'
      ];
      if (validNames.includes(styleId)) {
        return styleId;
      }
    }

    // Map common style IDs to API names
    const styleMap = {
      'bob-cut-001': 'BobCut',
      'bob-cut': 'BobCut',
      'pixie-cut-002': 'PixieCut',
      'pixie-cut': 'PixieCut',
      'long-layers-003': 'LongWavy',
      'long-layers': 'LongWavy',
      'short-bob-004': 'ShortNeatBob',
      'short-bob': 'ShortNeatBob',
      'wavy-bob-005': 'Soft_Wavy_Bob',
      'wavy-bob': 'Soft_Wavy_Bob',
      'curly-bob-006': 'CurlyBob',
      'curly-bob': 'CurlyBob'
    };

    // Try to find mapping
    const lowerStyleId = styleId?.toLowerCase();
    if (styleMap[lowerStyleId]) {
      return styleMap[lowerStyleId];
    }

    // If style ID contains the name, try to extract it
    // e.g., "bob-cut" -> "BobCut"
    if (lowerStyleId) {
      const parts = lowerStyleId.split(/[-_]/);
      const camelCase = parts.map((part, index) => 
        index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part.charAt(0).toUpperCase() + part.slice(1)
      ).join('');
      return camelCase;
    }

    // Default fallback
    return 'BobCut';
  }

  /**
   * Convert data URL to File object
   * @param {string} dataURL - Data URL string
   * @param {string} filename - Filename
   * @returns {Promise<File>} File object
   */
  async dataURLtoFile(dataURL, filename) {
    const response = await fetch(dataURL);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  }

  /**
   * Default hairstyles (fallback if API unavailable)
   */
  getDefaultHairstyles() {
    // Return default styles with correct API style names
    // These match the API's expected style names exactly
    return [
      { id: 'BobCut', styleId: 'BobCut', name: 'Bob Cut', thumbnail: '/placeholder-style.jpg' },
      { id: 'PixieCut', styleId: 'PixieCut', name: 'Pixie Cut', thumbnail: '/placeholder-style.jpg' },
      { id: 'LongWavy', styleId: 'LongWavy', name: 'Long Wavy', thumbnail: '/placeholder-style.jpg' },
      { id: 'ShortNeatBob', styleId: 'ShortNeatBob', name: 'Short Neat Bob', thumbnail: '/placeholder-style.jpg' },
      { id: 'Soft_Wavy_Bob', styleId: 'Soft_Wavy_Bob', name: 'Soft Wavy Bob', thumbnail: '/placeholder-style.jpg' },
      { id: 'CurlyBob', styleId: 'CurlyBob', name: 'Curly Bob', thumbnail: '/placeholder-style.jpg' },
      { id: 'BuzzCut', styleId: 'BuzzCut', name: 'Buzz Cut', thumbnail: '/placeholder-style.jpg' },
      { id: 'Ponytail', styleId: 'Ponytail', name: 'Ponytail', thumbnail: '/placeholder-style.jpg' }
    ];
  }

  /**
   * Default makeup styles (fallback if API unavailable)
   */
  getDefaultMakeupStyles() {
    return [
      { id: 'natural-001', name: 'Natural Look', thumbnail: '/styles/try-on/makeup/natural-thumb.jpg' },
      { id: 'glam-002', name: 'Glam Look', thumbnail: '/styles/try-on/makeup/glam-thumb.jpg' },
      { id: 'smoky-eye-003', name: 'Smoky Eye', thumbnail: '/styles/try-on/makeup/smoky-eye-thumb.jpg' },
      { id: 'bold-lips-004', name: 'Bold Lips', thumbnail: '/styles/try-on/makeup/bold-lips-thumb.jpg' },
      { id: 'bridal-005', name: 'Bridal Makeup', thumbnail: '/styles/try-on/makeup/bridal-thumb.jpg' },
      { id: 'party-006', name: 'Party Makeup', thumbnail: '/styles/try-on/makeup/party-thumb.jpg' }
    ];
  }

  /**
   * Check if API is configured
   * @returns {boolean}
   */
  isConfigured() {
    return !!AILAB_API_KEY;
  }
}

// Export singleton instance
export default new AILabToolsService();
